import React, { useEffect, useMemo, useState, useRef } from "react";
import { CButton, CCard, CCardGroup, CCol, CForm, CRow, CSpinner } from "@coreui/react";
import { dashboard, settings as settingsRoutes } from "@/libs/routes";
import { toast } from "react-toastify";
import { useTranslationContext } from "@/context/TranslationContext";
import { tab_key } from "@/constants/translation/common.translation";
import { useFormik } from "formik";
import CustomInput from "@/components/CustomInput";
import settings from "@/constants/translation/setting.translation";
import { useNavigate } from "react-router-dom";
import { edit_profile } from "@/constants/translation/auth.translation";
import { useGetSettingData, useUpdateSettingData } from "@/hooks";
import SettingValidationClass from "@/helpers/validations/setting";
import TabNavigation from "@/components/TabNavigation";
import constants from "@/libs/constant";

/**
 * @name ReferralSettings
 *
 * @description
 * The ReferralSettings component provides a form for managing the admin-tunable
 * Referral & Rewards program thresholds: how many active referrals unlock a reward,
 * how many months of premium each reward grants, and how many consecutive days a referred
 * user must log in (and how many chat messages they must send) to qualify as an
 * "Active User". A referral reward grants free premium *time*, not a purchased plan, so
 * there's no plan to choose here.
 *
 * The component:
 * - Loads existing referral settings on page load (generic AppSetting key/value store).
 * - Uses Formik for form state management and validation.
 * - Submits updated settings to the API.
 * - Displays success messages on successful update.
 *
 * @returns {JSX.Element} The JSX for rendering the referral & rewards settings form.
 */
const ReferralSettings = () => {
  const navigate = useNavigate();
  const t = useTranslationContext();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submissionBlockRef = useRef(false);

  const mutation = useUpdateSettingData(
    (response) => {
      toast.success(response.message); // Show success message on successful update
      doGetSettingData(); // Refetch data to get updated settings
      setTimeout(() => {
        submissionBlockRef.current = false;
        setIsSubmitting(false); // Reset submission state
      }, 1000); // Short delay to prevent immediate re-submission
    },
    (error) => {
      toast.error(error.response?.data?.message || 'Failed to update settings');
      setTimeout(() => {
        submissionBlockRef.current = false;
        setIsSubmitting(false); // Reset submission state on error
      }, 1000); // Short delay to prevent immediate re-submission
    }
  );

  const { isPending } = mutation;

  const validationSchema = useMemo(
    () => SettingValidationClass.referralSettingValidation(t),
    [t]
  );

  const formik = useFormik({
    initialValues: {
      [constants.formFields.referralSettings.milestoneSize]: "",
      [constants.formFields.referralSettings.monthsPerMilestone]: "",
      [constants.formFields.referralSettings.consecutiveDaysRequired]: "",
      [constants.formFields.referralSettings.minMessagesRequired]: "",
    },
    validationSchema,
    onSubmit: (values) => {
      if (submissionBlockRef.current || isSubmitting || isPending) {
        return; // Prevent multiple submissions
      }
      submissionBlockRef.current = true;
      setIsSubmitting(true);
      mutation.mutate(values);
    },
  });

  /**
   * !This API will call while Page Load and set data
   */
  const {
    refetch: doGetSettingData,
    isLoading: isLoadingData,
    data: getConfiguration,
  } = useGetSettingData();
  useEffect(() => {
    if (!isLoadingData && getConfiguration && getConfiguration.data) {
      const data = getConfiguration.data;

      formik.setValues({
        [constants.formFields.referralSettings.milestoneSize]: data.referral_milestone_size || "",
        [constants.formFields.referralSettings.monthsPerMilestone]: data.referral_months_per_milestone || "",
        [constants.formFields.referralSettings.consecutiveDaysRequired]: data.referral_consecutive_days_required || "",
        [constants.formFields.referralSettings.minMessagesRequired]: data.referral_min_messages_required || "",
      });
    }
  }, [getConfiguration, isLoadingData]); // eslint-disable-line react-hooks/exhaustive-deps

  const tabs = [
    {
      label: t(tab_key.settings.referral_settings),
      path: '',
    },
    {
      label: t(tab_key.settings.admin_rewards_settings),
      path: settingsRoutes.adminRewards,
    },
  ]
  return (
    <>
      <CCardGroup>
        <CCard className="p-4">
          <TabNavigation tabs={tabs} />
          {!isLoadingData && (
            <>
              <CForm onSubmit={formik.handleSubmit}>
                <CRow>
                  <CCol lg={6} md={6} sm={12}>
                    <CustomInput
                      label={t(settings.referral_settings.milestone_size_label)}
                      type="number"
                      name={constants.formFields.referralSettings.milestoneSize}
                      min={constants.validation.referralSettings.milestoneSizeMin}
                      max={constants.validation.referralSettings.milestoneSizeMax}
                      value={formik.values[constants.formFields.referralSettings.milestoneSize]}
                      onBlur={formik.handleBlur}
                      onChange={formik.handleChange}
                      error={
                        formik.touched[constants.formFields.referralSettings.milestoneSize] &&
                        formik.errors[constants.formFields.referralSettings.milestoneSize]
                      }
                    />
                  </CCol>
                  <CCol lg={6} md={6} sm={12}>
                    <CustomInput
                      label={t(settings.referral_settings.months_per_milestone_label)}
                      type="number"
                      name={constants.formFields.referralSettings.monthsPerMilestone}
                      min={constants.validation.referralSettings.monthsPerMilestoneMin}
                      max={constants.validation.referralSettings.monthsPerMilestoneMax}
                      value={formik.values[constants.formFields.referralSettings.monthsPerMilestone]}
                      onBlur={formik.handleBlur}
                      onChange={formik.handleChange}
                      error={
                        formik.touched[constants.formFields.referralSettings.monthsPerMilestone] &&
                        formik.errors[constants.formFields.referralSettings.monthsPerMilestone]
                      }
                    />
                  </CCol>
                </CRow>
                <CRow>
                  <CCol lg={6} md={6} sm={12}>
                    <CustomInput
                      label={t(settings.referral_settings.consecutive_days_label)}
                      type="number"
                      name={constants.formFields.referralSettings.consecutiveDaysRequired}
                      min={constants.validation.referralSettings.consecutiveDaysMin}
                      max={constants.validation.referralSettings.consecutiveDaysMax}
                      value={formik.values[constants.formFields.referralSettings.consecutiveDaysRequired]}
                      onBlur={formik.handleBlur}
                      onChange={formik.handleChange}
                      error={
                        formik.touched[constants.formFields.referralSettings.consecutiveDaysRequired] &&
                        formik.errors[constants.formFields.referralSettings.consecutiveDaysRequired]
                      }
                    />
                  </CCol>
                  <CCol lg={6} md={6} sm={12}>
                    <CustomInput
                      label={t(settings.referral_settings.min_messages_label)}
                      type="number"
                      name={constants.formFields.referralSettings.minMessagesRequired}
                      min={constants.validation.referralSettings.minMessagesMin}
                      max={constants.validation.referralSettings.minMessagesMax}
                      value={formik.values[constants.formFields.referralSettings.minMessagesRequired]}
                      onBlur={formik.handleBlur}
                      onChange={formik.handleChange}
                      error={
                        formik.touched[constants.formFields.referralSettings.minMessagesRequired] &&
                        formik.errors[constants.formFields.referralSettings.minMessagesRequired]
                      }
                    />
                  </CCol>
                </CRow>
                <CRow>
                  <CCol className="d-flex justify-content-center mt-2 gap-2">
                    <CButton
                      color="primary"
                      className="px-4"
                      type="submit"
                      disabled={isPending || isSubmitting || submissionBlockRef.current}
                    >
                      {(isPending || isSubmitting || submissionBlockRef.current) && <CSpinner size="sm" variant="grow" />}
                      {t(edit_profile.submit_btn_text)}
                    </CButton>
                    <CButton
                      color="secondary"
                      className="px-4"
                      type="button"
                      disabled={isPending || isSubmitting || submissionBlockRef.current}
                      onClick={() => {
                        navigate(dashboard);
                      }}
                    >
                      {t(edit_profile.cancel_button)}
                    </CButton>
                  </CCol>
                </CRow>
              </CForm>
            </>
          )}
        </CCard>
      </CCardGroup>
    </>
  );
};

export default ReferralSettings;
